"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.target = target;
const trainer_1 = require("../engine/trainer");
const assert_1 = require("./assert");
const engines_1 = require("./engines");
const shot_1 = require("./shot");
/**
 * 5 mm, or a looser simParams.stopDist. v1's 1 mm default is out of reach for
 * most targets once landings move in 2 cm jumps; tighter is not honored.
 */
const MIN_TOLERANCE = 0.005;
/** Legacy flights the correction may spend before the one that measures the result. */
const MAX_CORRECTIONS = 8;
/** The head pans at most this far either side of the body (yawpitch2launchpoint asserts it). */
const MAX_PAN_DEG = 90;
/** Bearing in degrees from one court point to another: 0 = +y, positive = clockwise. */
const bearingDeg = (from, to) => (Math.atan2(to.x - from.x, to.y - from.y) * 180.0) / Math.PI;
/** Wrap an angle in degrees to (-180, 180]. */
function wrapDeg(angle) {
    const w = ((angle % 360) + 360) % 360;
    return w > 180 ? w - 360 : w;
}
/** Height a planned shot targets: > 0 is an aerial target, 0 a landing. */
function targetHeight(plannedShot) {
    const { z } = plannedShot.target;
    return Number.isFinite(z) && z > 0 ? z : 0;
}
function target(ctx, plannedShot) {
    const forms = [plannedShot.launchAngle, plannedShot.peakHeight, plannedShot.speed ?? null].filter((v) => v !== null).length;
    (0, assert_1.assert)(forms === 1, "planned shot must have exactly 1 of (launch angle, peak height, speed)");
    const result = createTargetResult(ctx, plannedShot);
    if (!result.isValid) {
        return { result, launchPoint: null };
    }
    const { model } = ctx;
    const legacy = (0, engines_1.enginesFor)(model);
    // from here every plan needs the speed table (v1 computes NaN without one)
    (0, engines_1.assertLaunchable)(legacy.profile);
    if (plannedShot.speed != null) {
        if (!reachableWithoutDrag(ctx, plannedShot, result.launchPoint)) {
            return { result: invalid(result, `target out of reach at ${plannedShot.speed} m/s`), launchPoint: null };
        }
    }
    else if (plannedShot.peakHeight !== null && model.ballParams.drag === 0 && !(plannedShot.spin > 0)) {
        targetIdealPeakHeight(ctx, result);
        return { result, launchPoint: null };
    }
    const spin = shotSpin(plannedShot);
    if (spin.rpm > 0) {
        (0, assert_1.assert)(spin.axis >= 0 && spin.axis <= 360, `angle ${spin.axis} must be in [0, 360]`);
    }
    const plan = shotPlan(plannedShot, spin);
    const tolerance = Math.max(MIN_TOLERANCE, model.simParams.stopDist);
    const { solver } = legacy;
    const aimed = solver.aim(ctx.pose, plan);
    let attempt;
    if (aimed.ok) {
        attempt = correct(ctx.pose, plan, aimed, solver, legacy, tolerance);
    }
    else {
        attempt = aimed.closest ? { aim: aimed.closest, flights: 0 } : null;
        const message = failureMessage(aimed.reason, plannedShot, model, attempt !== null);
        if (message !== null) {
            invalid(result, message);
        }
    }
    if (attempt === null) {
        return { result, launchPoint: null };
    }
    const launchPoint = measure(ctx, result, attempt, legacy, tolerance);
    // v1's verdicts on the throw it ends with
    if (plannedShot.peakHeight !== null && attempt.aim.launch.speed >= model.limits.MPS.maxSafe && !result.isAccurate) {
        invalid(result, `out of reach: needs more than this trainer's ${model.limits.MPS.maxSafe.toFixed(1)} m/s`);
    }
    if (result.isValid && !Number.isFinite(result.errorDistance)) {
        invalid(result, `no solution: the flight never comes down through ${targetHeight(plannedShot)} m`);
    }
    return { result, launchPoint };
}
function invalid(result, message) {
    result.isValid = false;
    result.invalidMessage = message;
    return result;
}
/**
 * v1's message for an engine failure, or null where v1 would return the
 * closest throw as valid but inaccurate (it has no notion of a solve that
 * fails to converge, or of a throw blocked by the net).
 */
function failureMessage(reason, plannedShot, model, solved) {
    const { maxSafe } = model.limits.MPS;
    switch (reason) {
        case "targetBehind":
            return "target must be in front of trainer";
        case "targetTooClose":
            return solved ? `target too close for ${plannedShot.speed} m/s` : "launch and target point must be different";
        case "peakBelowLaunch":
            return "peak height must be >= launch height";
        case "peakBelowTarget":
            return "peak height must be >= target height";
        case "speedOutOfRange":
            return `speed must be in (0, ${maxSafe}] m/s`;
        case "outOfReach":
            if (plannedShot.speed != null) {
                return `target out of reach at ${plannedShot.speed} m/s`;
            }
            if (plannedShot.peakHeight !== null) {
                return `out of reach: needs more than this trainer's ${maxSafe.toFixed(1)} m/s`;
            }
            return `no solution at launch angle ${plannedShot.launchAngle ?? "n/a"}: the shot does not land`;
        case "blocked":
        case "noConvergence":
            return null;
    }
}
/**
 * v1 _createTargetResult: the initial result, and v1's validity rules in
 * v1's order, judged from the trainer's current launch point.
 */
function createTargetResult(ctx, plannedShot) {
    const result = {
        launchPoint: ctx.launchPointAt(0, 0),
        plannedShot,
        launchParams: {
            yaw: 0,
            pitch: 0,
            speedSpin: { speed: 0, spin: 0, spinAxis: 0, spinLevel: null },
            rpms: { left: 0, right: 0, top: 0 },
        },
        isValid: true,
        invalidMessage: "",
        isAccurate: false,
        errorDistance: -1,
        simIterations: -1,
        peakHeight: -1,
        pitchDeflection: 0,
        yawDeflection: 0,
    };
    const from = ctx.launchPoint;
    const dx = plannedShot.target.x - from.x;
    const dy = plannedShot.target.y - from.y;
    const range = Math.sqrt(dx * dx + dy * dy);
    if (Math.abs(wrapDeg(bearingDeg(from, plannedShot.target) - ctx.pose.yaw)) >= MAX_PAN_DEG) {
        invalid(result, "target must be in front of trainer");
    }
    else if (range < 0.1) {
        invalid(result, "launch and target point must be different");
    }
    else if (plannedShot.peakHeight !== null) {
        if (plannedShot.peakHeight - from.z < 0) {
            invalid(result, "peak height must be >= launch height");
        }
        else if (plannedShot.peakHeight < targetHeight(plannedShot)) {
            invalid(result, "peak height must be >= target height");
        }
    }
    else if (plannedShot.speed != null) {
        const { maxSafe } = ctx.model.limits.MPS;
        if (!(plannedShot.speed > 0) || plannedShot.speed > maxSafe) {
            invalid(result, `speed must be in (0, ${maxSafe}] m/s`);
        }
    }
    return result;
}
/** v1 _targetSpeed's first check: whether the target is in no-drag reach at the planned speed. */
function reachableWithoutDrag(ctx, plannedShot, from) {
    const v = plannedShot.speed;
    const g = ctx.model.constants.gravity;
    const { target: tp } = plannedShot;
    const range = Math.hypot(tp.x - from.x, tp.y - from.y);
    const dz = targetHeight(plannedShot) - from.z;
    return v ** 4 - g * (g * range * range + 2 * dz * v * v) >= 0;
}
/** v1 _targetIdealPeakHeight: the closed-form shot when there is neither drag nor spin. */
function targetIdealPeakHeight(ctx, result) {
    const lp = result.launchPoint;
    const tp = result.plannedShot.target;
    const peakH = result.plannedShot.peakHeight;
    const hDiff = peakH - lp.z;
    const g = ctx.model.constants.gravity;
    const dx = tp.x - lp.x;
    const dy = tp.y - lp.y;
    const range = Math.sqrt(dx * dx + dy * dy);
    const yawDegrees = wrapDeg(bearingDeg(lp, tp) - ctx.pose.yaw);
    const fall = peakH - targetHeight(result.plannedShot);
    const C = hDiff + Math.sqrt(fall * hDiff);
    const pitchDegrees = Math.atan((2.0 * C) / range) * (180.0 / Math.PI);
    const speed = Math.sqrt(2.0 * g * hDiff * (range * range + 4 * C * C)) / (2.0 * C);
    const { launchParams } = result;
    launchParams.yaw = yawDegrees;
    launchParams.pitch = pitchDegrees;
    result.launchPoint = ctx.launchPointAt(yawDegrees, pitchDegrees);
    launchParams.speedSpin.speed = speed;
    launchParams.speedSpin.spin = 0;
    launchParams.speedSpin.spinAxis = 0;
    launchParams.rpms = ctx.conversions.speedspin2rpm(launchParams.speedSpin);
}
/** The spin v1 throws a plan with: a peak-height plan drops spin that is not positive. */
function shotSpin(plannedShot) {
    if (plannedShot.peakHeight !== null && !(plannedShot.spin > 0)) {
        return { rpm: 0, axis: 0 };
    }
    return { rpm: plannedShot.spin, axis: plannedShot.spinAxis };
}
function shotPlan(plannedShot, spin) {
    const { target: t } = plannedShot;
    const target = { x: t.x, y: t.y, z: t.z };
    if (plannedShot.speed != null) {
        return { target, spin, shape: "speed", speed: plannedShot.speed };
    }
    if (plannedShot.peakHeight !== null) {
        return { target, spin, shape: "peakHeight", peakHeight: plannedShot.peakHeight };
    }
    return { target, spin, shape: "pitch", pitch: plannedShot.launchAngle };
}
/**
 * Re-aims until a legacy flight of the aim arrives within tolerance, or the
 * flights run out; returns the aim whose legacy flight came closest.
 */
function correct(pose, plan, first, solver, legacy, tolerance) {
    const { target } = plan;
    const z = Number.isFinite(target.z) && target.z > 0 ? target.z : 0;
    const until = z > 0 ? { kind: "descending", z } : { kind: "landing" };
    const options = { until };
    // misses split into along the throw (range) and across it
    const length = Math.hypot(target.x - first.origin.x, target.y - first.origin.y);
    const ux = (target.x - first.origin.x) / length;
    const uy = (target.y - first.origin.y) / length;
    let aim = first;
    let best = first;
    let bestMiss = Infinity;
    let flights = 0;
    let aimPoint = { x: target.x, y: target.y };
    let short = null;
    let long = null;
    for (;;) {
        const end = new ArrivalRecorder(z > 0);
        legacy.kernel.record((0, trainer_1.release)(legacy.profile, pose, aim.launch, "v1"), end, options);
        flights += 1;
        if (!end.arrived) {
            break;
        }
        const ex = end.x - target.x;
        const ey = end.y - target.y;
        const miss = Math.hypot(ex, ey);
        if (miss < bestMiss) {
            best = aim;
            bestMiss = miss;
        }
        if (miss <= tolerance || flights === MAX_CORRECTIONS) {
            break;
        }
        const along = ex * ux + ey * uy;
        if (along < 0) {
            short = aimPoint;
        }
        else {
            long = aimPoint;
        }
        if (short !== null && long !== null && flights >= 2) {
            // across the throw the landing is smooth: correct that part directly
            aimPoint = {
                x: (short.x + long.x) / 2 - (ex - along * ux),
                y: (short.y + long.y) / 2 - (ey - along * uy),
            };
        }
        else {
            aimPoint = { x: aimPoint.x - ex, y: aimPoint.y - ey };
        }
        const next = solver.aim(pose, { ...plan, target: { x: aimPoint.x, y: aimPoint.y, z: target.z } });
        if (!next.ok) {
            break;
        }
        aim = next;
    }
    return { aim: best, flights };
}
/**
 * Where a legacy flight arrives, as engine endpoints do: its first landing,
 * or its first descent through `z` (past net and wall contacts).
 */
class ArrivalRecorder {
    descending;
    arrived = false;
    x = NaN;
    y = NaN;
    constructor(descending) {
        this.descending = descending;
    }
    point() { }
    deflect() { }
    contact(_t, x, y, _z, _surface, kind) {
        if (kind === "ground" && !this.descending && !this.arrived) {
            this.arrive(x, y);
        }
    }
    truncate(_t, x, y) {
        this.arrive(x, y);
    }
    arrive(x, y) {
        this.arrived = true;
        this.x = x;
        this.y = y;
    }
}
/** v1 _crossingPoint: where a Shot descends through height z before its first bounce. */
function crossing({ ballPath, bounces }, z) {
    const bounceT = bounces.length > 0 ? bounces[0].timePosition.t : Infinity;
    for (let i = 1; i < ballPath.length && ballPath[i - 1].t < bounceT; i++) {
        const a = ballPath[i - 1].pos;
        const b = ballPath[i].pos;
        if (a.z >= z && b.z < z) {
            const f = (a.z - z) / (a.z - b.z);
            return { x: a.x + f * (b.x - a.x), y: a.y + f * (b.y - a.y) };
        }
    }
    return null;
}
/**
 * Flies the attempt as Launch would and fills the result from it, measured
 * as v1 measures: the landing (or the descent through an aerial target's
 * height before any contact), the whole flight's peak. Returns the trainer's
 * new launch point.
 */
function measure(ctx, result, attempt, legacy, tolerance) {
    const { model, pose } = ctx;
    const { plannedShot } = result;
    const { launch, deflection } = attempt.aim;
    (0, shot_1.assertFinish)(model.simParams, "Launch");
    const launchPoint = ctx.launchPointAt(launch.yaw, launch.pitch);
    const shot = new shot_1.ShotRecorder(launchPoint, model.simParams.duration, 0);
    legacy.kernel.record((0, trainer_1.release)(legacy.profile, pose, launch, "v1"), shot, { until: (0, shot_1.untilFor)(model.simParams) });
    const z = targetHeight(plannedShot);
    const { target: planned } = plannedShot;
    // v1 measures a throw that never lands from its unset landing point, (0, 0)
    const arrived = z > 0 ? crossing(shot, z) : shot.landingPoint;
    const errorDistance = arrived === null ? Infinity : Math.hypot(planned.x - arrived.x, planned.y - arrived.y);
    result.launchParams = launchParamsFor(plannedShot, launch, ctx.conversions);
    result.launchPoint = { ...launchPoint };
    result.peakHeight = shot.peakHeight;
    result.pitchDeflection = deflection.pitch;
    result.yawDeflection = deflection.yaw;
    result.errorDistance = errorDistance;
    // v1 counts the flights it launched; so does compat, its legacy ones
    result.simIterations = attempt.flights + 1;
    result.isAccurate = errorDistance <= tolerance;
    return launchPoint;
}
/** The launch parameters, shaped as each of v1's solvers leaves them. */
function launchParamsFor(plannedShot, launch, conversions) {
    const { pitch, yaw, speed } = launch;
    let speedSpin;
    if (plannedShot.peakHeight !== null) {
        const spun = plannedShot.spin > 0;
        speedSpin = { speed, spin: spun ? plannedShot.spin : 0, spinAxis: spun ? plannedShot.spinAxis : 0, spinLevel: null };
    }
    else {
        speedSpin = { speed, spin: plannedShot.spin, spinAxis: plannedShot.spinAxis, spinLevel: null };
    }
    return { pitch, yaw, speedSpin, rpms: conversions.speedspin2rpm(speedSpin) };
}
